'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import type {
  BaseFormFieldProps,
  FormOption,
  InfiniteSelectConfig
} from '@/types/base-form';

interface FormMultiComboboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends BaseFormFieldProps<TFieldValues, TName> {
  options?: FormOption[];
  infiniteConfig?: InfiniteSelectConfig;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

function FormMultiCombobox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  description,
  required,
  options,
  infiniteConfig,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  className
}: FormMultiComboboxProps<TFieldValues, TName>) {
  const tc = useTranslations('common');
  const [open, setOpen] = useState(false);

  const isInfiniteMode = !!infiniteConfig;
  const resolvedOptions = infiniteConfig?.options ?? options ?? [];

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open && infiniteConfig) {
      infiniteConfig.onSearchChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchGuardRef = useRef(false);

  useEffect(() => {
    if (!infiniteConfig?.isFetchingNextPage) {
      fetchGuardRef.current = false;
    }
  }, [infiniteConfig?.isFetchingNextPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !infiniteConfig || fetchGuardRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (
      scrollHeight - scrollTop - clientHeight < 80 &&
      infiniteConfig.hasNextPage
    ) {
      fetchGuardRef.current = true;
      infiniteConfig.fetchNextPage();
    }
  }, [infiniteConfig]);

  useEffect(() => {
    if (!open || !isInfiniteMode) return;
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [open, isInfiniteMode, handleScroll]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedValues: string[] = Array.isArray(field.value)
          ? field.value
          : [];

        function toggleValue(val: string) {
          const next = selectedValues.includes(val)
            ? selectedValues.filter((v) => v !== val)
            : [...selectedValues, val];
          field.onChange(next);
        }

        return (
          <FormItem className={className}>
            {label && (
              <FormLabel>
                {label}
                {required && <span className='ms-1 text-red-500'>*</span>}
              </FormLabel>
            )}
            <Popover open={open} onOpenChange={setOpen}>
              <FormControl>
                <PopoverTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    role='combobox'
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                      'border-input h-9 w-full justify-between bg-transparent px-3 font-normal shadow-xs',
                      selectedValues.length === 0 && 'text-muted-foreground'
                    )}
                  >
                    <span className='truncate'>
                      {selectedValues.length > 0
                        ? tc('nSelected', { count: selectedValues.length })
                        : (placeholder ?? tc('search'))}
                    </span>
                    <ChevronsUpDown className='text-muted-foreground ms-2 h-4 w-4 shrink-0 opacity-50' />
                  </Button>
                </PopoverTrigger>
              </FormControl>
              <PopoverPrimitive.Content
                align='start'
                sideOffset={4}
                className='bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-(--radix-popover-trigger-width) origin-(--radix-popover-content-transform-origin) rounded-md border p-0 shadow-md outline-hidden'
              >
                <Command shouldFilter={!isInfiniteMode}>
                  <CommandInput
                    placeholder={searchPlaceholder ?? tc('search')}
                    value={isInfiniteMode ? infiniteConfig.search : undefined}
                    onValueChange={
                      isInfiniteMode ? infiniteConfig.onSearchChange : undefined
                    }
                  />
                  <div
                    ref={scrollRef}
                    className='max-h-[300px] overflow-y-auto'
                    onScroll={isInfiniteMode ? handleScroll : undefined}
                  >
                    <CommandList className='max-h-none overflow-visible'>
                      {infiniteConfig?.isLoading ? (
                        <div className='flex items-center justify-center py-6'>
                          <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>
                            {emptyMessage ?? tc('noResults')}
                          </CommandEmpty>
                          <CommandGroup>
                            {resolvedOptions.map((option) => (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                                onSelect={() => toggleValue(option.value)}
                              >
                                <span className='flex-1 truncate'>
                                  {option.label}
                                </span>
                                <Check
                                  className={cn(
                                    'ms-auto h-4 w-4 shrink-0',
                                    selectedValues.includes(option.value)
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          {isInfiniteMode &&
                            infiniteConfig.isFetchingNextPage && (
                              <div className='flex items-center justify-center py-2'>
                                <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                              </div>
                            )}
                        </>
                      )}
                    </CommandList>
                  </div>
                </Command>
              </PopoverPrimitive.Content>
            </Popover>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export { FormMultiCombobox };
